import axios from "axios";
import config from "../config";
import Labels from "../services/apiLabels";
import { getAppointmentSummaryParts } from "./appointmentBooking";
import { formatAuthDate } from "./userIdentity";

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") {
    if (Array.isArray(value.results)) return value.results;
    if (Array.isArray(value.data)) return value.data;
    if (Array.isArray(value.history)) return value.history;
    if (Array.isArray(value.items)) return value.items;
  }
  return [];
}

function unwrapPayload(payload) {
  if (!payload || typeof payload !== "object") return {};
  return (
    payload.data?.user ||
    payload.user ||
    payload.data ||
    payload ||
    {}
  );
}

export function pickCredits(payload) {
  const user = unwrapPayload(payload);
  const value =
    user.credit ??
    user.credits ??
    user.available_credits ??
    user.available_credit ??
    user.no_of_credits ??
    user.wallet_balance ??
    user.balance ??
    user.credit_balance ??
    payload?.credit ??
    payload?.credits ??
    payload?.available_credits ??
    payload?.available_credit ??
    payload?.no_of_credits ??
    payload?.data?.credit ??
    payload?.data?.credits ??
    payload?.data?.available_credits;
  if (value == null || value === "") return null;
  return value;
}

export function pickLastEvaluation(payload) {
  const user = unwrapPayload(payload);
  return (
    user.last_evaluation ||
    user.lastEvaluation ||
    user.last_evaluate ||
    user.last_evaluate_date ||
    payload?.last_evaluation ||
    payload?.lastEvaluation ||
    payload?.data?.last_evaluation ||
    payload?.data?.lastEvaluation ||
    ""
  );
}

function toDateMs(value) {
  if (!value) return NaN;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : NaN;
}

/**
 * Newest created/updated date from normalized evaluation rows (or raw items).
 */
export function pickLatestEvaluationDate(evaluations) {
  if (!Array.isArray(evaluations) || !evaluations.length) return "";
  let best = "";
  let bestMs = -Infinity;
  for (let i = 0; i < evaluations.length; i += 1) {
    const item = evaluations[i];
    if (!item || typeof item !== "object") continue;
    const raw = item.raw && typeof item.raw === "object" ? item.raw : item;
    const candidates = [
      item.date,
      raw.created_at,
      raw.created,
      raw.date,
      raw.updated_at,
      raw.last_evaluation,
      raw.evaluation_date,
    ];
    for (let j = 0; j < candidates.length; j += 1) {
      const value = candidates[j];
      const ms = toDateMs(value);
      if (!Number.isFinite(ms)) continue;
      if (ms > bestMs) {
        bestMs = ms;
        best = value;
      }
    }
  }
  return best || "";
}

/**
 * Prefer the chronologically newest date between profile and evaluations list.
 */
export function resolveLastEvaluation(profileDate, evaluations) {
  const fromList = pickLatestEvaluationDate(evaluations);
  const profileMs = toDateMs(profileDate);
  const listMs = toDateMs(fromList);
  if (Number.isFinite(listMs) && (!Number.isFinite(profileMs) || listMs >= profileMs)) {
    return fromList;
  }
  if (profileDate) return profileDate;
  return fromList || "";
}

export function pickCreditHistory(payload) {
  const root = payload || {};
  if (Array.isArray(root)) {
    return root.map(normalizeCreditHistoryItem).filter(Boolean);
  }
  const user = unwrapPayload(payload);
  const candidates = [
    root.results,
    root.data?.results,
    root.data,
    root.transactions,
    root.credit_transactions,
    root.credit_history,
    root.credits_history,
    root.wallet_history,
    root.ledger,
    root.items,
    user.credit_history,
    user.credits_history,
    user.credit_transactions,
    user.transactions,
    user.wallet_history,
    user.ledger,
    root.data?.credit_history,
    root.data?.credits_history,
    root.data?.transactions,
    root.data?.data,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const list = asArray(candidates[i]);
    if (list.length) return list.map(normalizeCreditHistoryItem).filter(Boolean);
  }
  return [];
}

function firstDefined(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (value != null && value !== "") return value;
  }
  return "";
}

function toFiniteNumber(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return null;
  if (typeof value === "object") {
    return toFiniteNumber(
      value.amount ??
        value.value ??
        value.points ??
        value.credits ??
        value.credit ??
        value.debit ??
        null
    );
  }
  const cleaned = String(value).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  if (!cleaned) return null;
  const num = Number(cleaned[0]);
  return Number.isFinite(num) ? num : null;
}

/**
 * Pull a signed points delta from a credit-transaction row.
 * Primary API field: `no_of_credits` (positive = credit, negative = debit).
 */
export function pickCreditAmount(item = {}) {
  if (!item || typeof item !== "object") return null;

  // Exact API contract for user_credit_transactions
  const primary = toFiniteNumber(item.no_of_credits);
  if (primary != null) return primary;

  const typeRaw = String(
    firstDefined(
      item.event_type_name,
      item.type,
      item.direction,
      item.kind,
      item.transaction_type,
      item.credit_type,
      item.entry_type,
      item.credit_or_debit,
      item.credit_debit,
      item.action,
      item.mode
    )
  ).trim();
  const typeLower = typeRaw.toLowerCase();
  const looksDebit =
    item.is_debit === true ||
    /(debit|spend|deduct|minus|withdraw|out|used|redeem)/i.test(typeLower);
  const looksCredit =
    item.is_credit === true ||
    /(credit|earn|reward|bonus|deposit|in|added|gain|register)/i.test(
      typeLower
    );

  const namedCandidates = [
    item.amount,
    item.credits,
    item.credit_points,
    item.points,
    item.point,
    item.value,
    item.credit_amount,
    item.debit_amount,
    item.transaction_amount,
    item.change,
    item.delta,
    item.balance_change,
    item.qty,
    item.quantity,
    item.no_of_credit,
    item.no_of_points,
    item.number_of_credits,
    item.number_of_points,
    item.num_credits,
    item.num_points,
    item.total_credits,
    item.total_points,
    item.credit_value,
    item.point_value,
    item.points_earned,
    item.points_spent,
    item.credits_added,
    item.credits_deducted,
  ];

  for (let i = 0; i < namedCandidates.length; i += 1) {
    const num = toFiniteNumber(namedCandidates[i]);
    if (num == null) continue;
    if (looksDebit && num > 0) return -Math.abs(num);
    if (looksCredit && num < 0) return Math.abs(num);
    return num;
  }

  const debitOnly = toFiniteNumber(item.debit);
  const creditOnly = toFiniteNumber(item.credit);
  if (
    debitOnly != null &&
    typeof item.debit !== "boolean" &&
    (creditOnly == null || creditOnly === 0)
  ) {
    return -Math.abs(debitOnly);
  }
  if (
    creditOnly != null &&
    typeof item.credit !== "boolean" &&
    String(item.credit).toLowerCase() !== "credit" &&
    (debitOnly == null || debitOnly === 0)
  ) {
    return Math.abs(creditOnly);
  }

  return null;
}

/**
 * Sum signed credit/debit amounts from normalized or raw history rows.
 * Useful when profile balance is missing but ledger rows are present.
 */
export function sumCreditHistoryAmounts(items) {
  if (!Array.isArray(items) || !items.length) return null;
  let total = 0;
  let sawAmount = false;
  for (let i = 0; i < items.length; i += 1) {
    const row = items[i];
    const amount =
      row && typeof row === "object" && "amount" in row
        ? toFiniteNumber(row.amount)
        : pickCreditAmount(row);
    if (amount == null) continue;
    sawAmount = true;
    total += amount;
  }
  return sawAmount ? total : null;
}

/**
 * Prefer a full ledger sum when we have transaction rows; otherwise profile.
 */
export function resolveAvailableCredits(
  profileCredits,
  creditHistory,
  { preferLedger = false } = {}
) {
  const ledger = sumCreditHistoryAmounts(creditHistory);
  if (preferLedger && ledger != null) return ledger;
  if (profileCredits != null && profileCredits !== "") return profileCredits;
  return ledger;
}

/**
 * Walk all credit-transaction pages and return rows + summed balance.
 */
export async function fetchCreditLedgerBalance({
  token,
  userId,
  pageSize = 100,
} = {}) {
  let page = 1;
  const all = [];
  let count = 0;

  for (;;) {
    const parsed = await fetchUserCreditTransactions({
      token,
      userId,
      page,
      pageSize,
    });
    count = Number(parsed.count) || 0;
    const batch = parsed.results || [];
    all.push(...batch);
    if (!parsed.next || batch.length === 0 || all.length >= count || page >= 50) {
      break;
    }
    page += 1;
  }

  return {
    results: all,
    count: count || all.length,
    balance: sumCreditHistoryAmounts(all),
  };
}

export function humanizeEventTypeName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  return raw
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}

export function normalizeCreditHistoryItem(item, index = 0) {
  if (item == null) return null;
  if (typeof item !== "object") {
    return {
      id: `credit-${index}`,
      title: String(item),
      amount: null,
      date: "",
      status: "",
      type: "",
      vehicle: "",
    };
  }

  const amount = pickCreditAmount(item);
  const eventName = humanizeEventTypeName(
    item.event_type_name || item.event_name || ""
  );
  const title =
    firstDefined(
      eventName,
      item.title,
      item.description,
      item.reason,
      item.note,
      amount != null
        ? Number(amount) < 0
          ? "Debit"
          : "Credit"
        : "Activity"
    ) || "Activity";

  const date = firstDefined(
    item.date_of_transaction,
    item.created_at,
    item.created,
    item.activity_date,
    item.transaction_date,
    item.date,
    item.timestamp,
    item.updated_at
  );

  const type =
    amount != null && Number.isFinite(Number(amount))
      ? Number(amount) < 0
        ? "debit"
        : "credit"
      : "";

  return {
    id: item.id || item.pk || `credit-${index}`,
    title: String(title),
    amount,
    date,
    status: item.is_active === false ? "Inactive" : "",
    type,
    vehicle: String(item.vehicle_variant || item.variant_name || "").trim(),
    eventType: item.event_type,
    eventTypeName: item.event_type_name || "",
  };
}

export function formatCreditAmount(amount) {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  if (!Number.isFinite(num)) return String(amount);
  if (num === 0) return "0 pts";
  const sign = num > 0 ? "+" : "";
  return `${sign}${num} pts`;
}

export function creditAmountTone(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num) || num === 0) return "neutral";
  return num > 0 ? "credit" : "debit";
}

export function deriveEvaluationStatus(item = {}) {
  const explicit = firstDefined(
    item.status,
    item.evaluation_status,
    item.appointment_status,
    item.state,
    item.stage,
    item.current_status
  );
  if (explicit) {
    return {
      label: String(explicit).replace(/_/g, " "),
      tone: String(explicit).toLowerCase().includes("complete")
        ? "done"
        : String(explicit).toLowerCase().includes("cancel")
          ? "cancel"
          : String(explicit).toLowerCase().includes("book")
            ? "booked"
            : "pending",
    };
  }

  const appointment = Array.isArray(item.appointment)
    ? item.appointment[0]
    : item.appointment;
  const { location, date, time } = getAppointmentSummaryParts(appointment || {});
  const hasBooking = Boolean(location || date || time);

  if (hasBooking) {
    return { label: "Booked", tone: "booked" };
  }
  if (item.tracking_number) {
    return { label: "In progress", tone: "pending" };
  }
  return { label: "No booking", tone: "pending" };
}

export function normalizeEvaluationItem(item, index = 0) {
  if (!item || typeof item !== "object") return null;

  const appointment = Array.isArray(item.appointment)
    ? item.appointment[0]
    : item.appointment;
  const summary = getAppointmentSummaryParts(appointment || {});
  const status = deriveEvaluationStatus(item);
  const title = [item.brand_name, item.model_name, item.year_name]
    .filter(Boolean)
    .join(" ");

  return {
    id: item.id || item.pk || item.evaluation_id || `eval-${index}`,
    title: title || item.car || item.reg_number || "Vehicle evaluation",
    reg: item.reg_number || item.car || "",
    tracking: item.tracking_number || "",
    date:
      item.created_at ||
      item.created ||
      item.date ||
      summary.date ||
      item.last_evaluation ||
      "",
    location: summary.location,
    time: summary.time,
    statusLabel: status.label,
    statusTone: status.tone,
    raw: item,
  };
}

export function pickEvaluationsList(payload) {
  const root = payload || {};
  const user = unwrapPayload(payload);
  const candidates = [
    root.data,
    root.results,
    root.evaluations,
    root.user_evaluations,
    user.evaluations,
    user.user_evaluations,
    user.evaluation_list,
    root.data?.evaluations,
    root.data?.results,
    root.data?.data,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const list = asArray(candidates[i]);
    if (list.length && typeof list[0] === "object") {
      return list.map(normalizeEvaluationItem).filter(Boolean);
    }
  }

  // Single evaluation object
  if (user.id && (user.reg_number || user.brand_name || user.tracking_number)) {
    const one = normalizeEvaluationItem(user);
    return one ? [one] : [];
  }
  return [];
}

export async function fetchProfileActivity({ userId, token }) {
  const response = await axios.get(
    `${config.baseUrl}${Labels.getupdateprofile}${userId}/`,
    { headers: { Authorization: `Token ${token}` } }
  );
  return {
    credits: pickCredits(response.data),
    lastEvaluation: pickLastEvaluation(response.data),
    user: unwrapPayload(response.data),
    raw: response.data,
  };
}

/**
 * Re-fetch wallet balance + last evaluation after a debit-capable step.
 * Credits prefer ledger sum; last evaluation prefers newest evaluation row.
 * Returns { credits, lastEvaluation } or null.
 */
export async function refreshAuthCredits({ userId, token } = {}) {
  const id = String(userId || "").trim();
  const tok = String(token || "").trim();
  if (!id || !tok) return null;
  try {
    const [profile, ledger, evaluationsResult] = await Promise.all([
      fetchProfileActivity({ userId: id, token: tok }),
      fetchCreditLedgerBalance({ token: tok, userId: id }).catch(() => null),
      fetchUserEvaluations({ token: tok, userId: id }).catch(() => null),
    ]);
    const evaluations = (evaluationsResult?.list || []).filter((item) => {
      if (!item?.raw) return true;
      return filterEvaluationsByUserId([item.raw], id).length > 0;
    });
    return {
      credits: resolveAvailableCredits(
        profile.credits,
        ledger?.results || [],
        { preferLedger: ledger?.balance != null }
      ),
      lastEvaluation: resolveLastEvaluation(
        profile.lastEvaluation,
        evaluations
      ),
    };
  } catch {
    return null;
  }
}

/** Dispatch UPDATE_AUTH so Credits & evaluations popup stays in sync. */
export async function syncAuthCredits({
  userId,
  token,
  dispatch,
  updateAuthType = "UPDATE_AUTH",
} = {}) {
  const result = await refreshAuthCredits({ userId, token });
  if (!result) return result;
  const payload = {};
  if (result.credits != null) payload.credits = result.credits;
  if (result.lastEvaluation) payload.last_evaluation = result.lastEvaluation;
  if (!Object.keys(payload).length) return result;

  if (typeof dispatch === "function") {
    dispatch({ type: updateAuthType, payload });
  }

  // Always patch the main client store (dealers popup uses it).
  try {
    const clientStore = require("../store").default;
    if (clientStore?.store?.dispatch) {
      clientStore.store.dispatch({ type: updateAuthType, payload });
    }
  } catch {
    /* ignore */
  }

  return result;
}

export async function fetchUserEvaluations({ token, userId }) {
  const params = {};
  if (userId) params.user_id = userId;
  const response = await axios.get(
    `${config.baseUrl}${Labels.user_evaluations}`,
    {
      headers: { Authorization: `Token ${token}` },
      params,
    }
  );
  return {
    list: pickEvaluationsList(response.data),
    raw: pickEvaluationsRaw(response.data),
    payload: response.data,
  };
}

export function parseCreditTransactionsResponse(
  payload,
  { page = 1, pageSize = 10 } = {}
) {
  const root = payload || {};
  const results = Array.isArray(root.results)
    ? root.results.map(normalizeCreditHistoryItem).filter(Boolean)
    : pickCreditHistory(root);
  const count = Number.isFinite(Number(root.count))
    ? Number(root.count)
    : results.length;

  return {
    results,
    count,
    next: root.next || null,
    previous: root.previous || null,
    page: Number(page) || 1,
    pageSize: Number(pageSize) || 10,
    totalPages: Math.max(1, Math.ceil(count / (Number(pageSize) || 10))),
  };
}

export async function fetchUserCreditTransactions({
  token,
  userId,
  page = 1,
  pageSize = 10,
} = {}) {
  const params = {
    page,
    page_size: pageSize,
  };
  if (userId) params.user_id = userId;
  const response = await axios.get(
    `${config.baseUrl}${Labels.user_credit_transactions}`,
    {
      headers: { Authorization: `Token ${token}` },
      params,
    }
  );
  return parseCreditTransactionsResponse(response.data, { page, pageSize });
}

/** Raw evaluation objects for appointment cards (same shape as API items). */
export function pickEvaluationsRaw(payload) {
  const root = payload || {};
  const user = unwrapPayload(payload);
  const candidates = [
    root.data,
    root.results,
    root.evaluations,
    root.user_evaluations,
    user.evaluations,
    user.user_evaluations,
    user.evaluation_list,
    root.data?.evaluations,
    root.data?.results,
    root.data?.data,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const list = asArray(candidates[i]);
    if (list.length && typeof list[0] === "object") {
      return list.filter(Boolean);
    }
  }

  if (user.id && (user.reg_number || user.brand_name || user.tracking_number)) {
    return [user];
  }
  return [];
}

/**
 * Keep evaluations that belong to this user id when the payload includes user refs.
 * If items have no user field, keep them (token-scoped API already filtered).
 */
export function filterEvaluationsByUserId(items, userId) {
  const id = String(userId || "").trim();
  if (!id || !Array.isArray(items)) return Array.isArray(items) ? items : [];
  return items.filter((item) => {
    if (!item || typeof item !== "object") return false;
    const refs = [
      item.user_id,
      item.user,
      item.client_id,
      item.owner_id,
      item.created_by,
      item.userid,
    ];
    const hasRef = refs.some(
      (ref) =>
        ref != null &&
        ref !== "" &&
        (typeof ref === "object"
          ? ref.id != null || ref.pk != null
          : true)
    );
    if (!hasRef) return true;
    return refs.some((ref) => {
      if (ref == null || ref === "") return false;
      if (typeof ref === "object") {
        return String(ref.id ?? ref.pk ?? "") === id;
      }
      return String(ref) === id;
    });
  });
}

export async function loadAccountActivity({
  userId,
  token,
  creditPage = 1,
  creditPageSize = 5,
} = {}) {
  const authHeaders = { Authorization: `Token ${token}` };
  const txParams = {
    page: creditPage,
    page_size: creditPageSize,
    ...(userId ? { user_id: userId } : {}),
  };
  const evalParams = userId ? { user_id: userId } : undefined;

  const [profileResult, transactionsResult, evaluationsResult, ledgerResult] =
    await Promise.allSettled([
      fetchProfileActivity({ userId, token }),
      axios.get(`${config.baseUrl}${Labels.user_credit_transactions}`, {
        headers: authHeaders,
        params: txParams,
      }),
      axios.get(`${config.baseUrl}${Labels.user_evaluations}`, {
        headers: authHeaders,
        params: evalParams,
      }),
      fetchCreditLedgerBalance({ token, userId, pageSize: 100 }),
    ]);

  const profile =
    profileResult.status === "fulfilled"
      ? profileResult.value
      : {
          credits: null,
          lastEvaluation: "",
          user: {},
        };

  let creditHistory = [];
  let creditHistoryError = "";
  let creditCount = 0;
  let creditNext = null;
  let creditPrevious = null;
  if (transactionsResult.status === "fulfilled") {
    const parsed = parseCreditTransactionsResponse(
      transactionsResult.value.data,
      { page: creditPage, pageSize: creditPageSize }
    );
    creditHistory = parsed.results;
    creditCount = parsed.count;
    creditNext = parsed.next;
    creditPrevious = parsed.previous;
  } else {
    creditHistoryError = "Could not load credit history.";
  }

  let ledgerBalance = null;
  let ledgerRows = [];
  if (ledgerResult.status === "fulfilled") {
    ledgerBalance = ledgerResult.value.balance;
    ledgerRows = ledgerResult.value.results || [];
    if (!creditCount) creditCount = ledgerResult.value.count || ledgerRows.length;
    // If the preview page failed but ledger succeeded, seed history from ledger.
    if (!creditHistory.length && ledgerRows.length) {
      creditHistory = ledgerRows.slice(0, creditPageSize);
      creditHistoryError = "";
    }
  }

  let evaluations = [];
  let evaluationsError = "";
  if (evaluationsResult.status === "fulfilled") {
    evaluations = pickEvaluationsList(evaluationsResult.value.data).filter(
      (item) => {
        if (!userId || !item?.raw) return true;
        return filterEvaluationsByUserId([item.raw], userId).length > 0;
      }
    );
  } else {
    evaluationsError = "Could not load evaluations.";
  }

  // Available credits = sum of all ledger no_of_credits when possible.
  const credits = resolveAvailableCredits(profile.credits, ledgerRows, {
    preferLedger: ledgerBalance != null,
  });
  const lastEvaluation = resolveLastEvaluation(
    profile.lastEvaluation,
    evaluations
  );

  return {
    credits,
    lastEvaluation,
    creditHistory,
    creditHistoryError,
    creditCount,
    creditNext,
    creditPrevious,
    creditPage,
    creditPageSize,
    evaluations,
    evaluationsError,
    user: profile.user,
    ledgerBalance,
  };
}

export function formatActivityDate(value) {
  return formatAuthDate(value) || String(value || "").trim() || "—";
}
